import { Formik, Form } from 'formik';
import { useState } from 'react';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';

import Button from 'hb-react/backoffice/components/Button';
import StripeError from 'hb-react/frontoffice/components/payments/PaymentStripe/errors/StripeError';
import {
  displayAmount,
  displayCurrency,
  cartItemWithRecurringPayment,
  getReturnUrl,
} from 'hb-react/frontoffice/components/payments/PaymentStripe/utils';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { Text } from 'hb-react/shared/components/formik/components/inputs';
import Loader from 'hb-react/shared/components/Loader';
import { displayNotif } from 'hb-react/shared/helpers/notifications';
import { axiosInstance as axios } from 'hb-react/shared/utils/axios';
import * as errorReporting from 'hb-react/shared/utils/errorReporting';

import MandateText from '../MandateText';

const BancontactForm = ({
  amount,
  currency,
  endpointUrl,
  endpointData,
  stripe,
  paymentAccountConfig,
}) => {
  const { currentUser } = useDefaultAppContexts();
  const [loading, setLoading] = useState(false);

  const recurringPaymentEnabled = cartItemWithRecurringPayment(endpointData);

  const handleError = (error) => {
    displayNotif(
      'error',
      s__('StripeBancontact|An error occurred processing the payment'),
    );
    setLoading(false);
    errorReporting.captureException(error);
  };

  const confirmPaymentIntent = (
    clientSecret,
    cartId,
    accountholderName,
    accountholderEmail,
  ) => {
    const returnUrl = getReturnUrl({ cart_id: cartId });

    const billingDetails = {
      name: accountholderName,
    };

    if (recurringPaymentEnabled) {
      billingDetails.email = accountholderEmail;
    }

    stripe
      .confirmBancontactPayment(clientSecret, {
        payment_method: {
          billing_details: billingDetails,
        },
        return_url: returnUrl,
      })
      .then(({ error }) => {
        if (error) {
          handleError(new StripeError('Error confirming bancontact payment'));
        }
      });
  };

  const setupPayment = () => {
    const body = endpointData({
      payment_method: 'bancontact',
      gateway: 'Stripe',
    });

    return axios.post(endpointUrl, body).catch((error) => {
      handleError(
        new StripeError('Error creating bancontact payment intent', error),
      );
    });
  };

  const handleSubmit = ({ accountholderName, accountholderEmail }) => {
    setLoading(true);
    setupPayment().then(({ data }) => {
      const { client_secret: clientSecret, cart_id: cartId } = data;

      confirmPaymentIntent(
        clientSecret,
        cartId,
        accountholderName,
        accountholderEmail,
      );
    });
  };

  const initialValues = {
    accountholderName: currentUser?.name || '',
    accountholderEmail: currentUser?.email || '',
  };

  return (
    <Card>
      <Formik
        onSubmit={handleSubmit}
        className="push-double"
        initialValues={initialValues}
      >
        {() => (
          <Form>
            {loading && <Loader fillParent />}
            <Box flexDirection="column" mb={2}>
              <Text
                name="accountholderName"
                label={s__('StripeBancontact|Accountholder Name')}
                validate={{
                  required: true,
                }}
              />
              {recurringPaymentEnabled && (
                <Text
                  name="accountholderEmail"
                  label={s__('StripeBancontact|Accountholder Email')}
                  validate={{
                    required: true,
                  }}
                />
              )}

              {recurringPaymentEnabled && (
                <MandateText
                  merchantDisplayName={
                    paymentAccountConfig.merchant_display_name
                  }
                />
              )}

              <Button
                className="push--top"
                data-testid="stripe-sepa-debit-form-submit"
                type="submit"
                purpose="primary"
                onSubmit={handleSubmit}
              >
                {__('Pay %{currency} %{amount}').i__({
                  currency: displayCurrency(currency),
                  amount: displayAmount(amount),
                })}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Card>
  );
};

export default BancontactForm;
